import { memo, useMemo } from 'react';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, currencyFmt } from './shared';
import type { OpenTicketsDashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: OpenTicketsDashboardWidget;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

const numberFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });

const DEFAULT_SECTION_ORDER = ['Restoran', 'Otel'];
const DEFAULT_LATE_HOURS: Record<string, number> = { Restoran: 3, Otel: 48 };

interface Ticket {
  tip: string;
  bolum: string;
  masa: string;
  kapasite: number;
  acilis: string;
  acikDk: number;
  tutar: number;
}

interface BolumGroup {
  bolum: string;
  adet: number;
  tutar: number;
  rows: Ticket[];
}

interface TipSection {
  tip: string;
  adet: number;
  tutar: number;
  bolumler: BolumGroup[];
}

function num(v: unknown): number {
  return Number(v) || 0;
}

/** 45 → "45dk", 72 → "1sa 12dk", 2820 → "47sa 00dk" (dakika kolon hizası için padlenir). */
export function formatDuration(dk: number): string {
  // Negatif süre ERP veri-giriş hatası (gelecek tarihli TARIH) veya ERP-DB
  // saat kaymasıyla gelebilir. Kırpmazsak `Math.floor(-90/60) = -2`,
  // `-90 % 60 = -30` → "-30dk" gibi anlamsız bir değer render edilir.
  if (dk <= 0) return '0dk';
  const saat = Math.floor(dk / 60);
  const dakika = dk % 60;
  if (saat <= 0) return `${dakika}dk`;
  return `${saat}sa ${String(dakika).padStart(2, '0')}dk`;
}

/**
 * "2026-07-18T13:28:36" → "13:28".
 *
 * Ayna fiş saatleri ZATEN İstanbul lokali. `new Date(iso)` kullanılsaydı
 * değer tarayıcının saat dilimine göre kayardı (ve "Z" ekli değerlerde
 * ikinci bir kayma daha eklenirdi). Bu yüzden saat metinden okunuyor.
 */
export function formatClock(iso: string): string {
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[1]}:${m[2]}` : '—';
}

function OpenTicketsWidgetImpl({ widget, sourceId, parameters }: Props) {
  const { config } = widget;
  const sectionOrder = config.sectionOrder ?? DEFAULT_SECTION_ORDER;
  const lateAfterHours = config.lateAfterHours ?? DEFAULT_LATE_HOURS;
  const etiket = { acik: 'Açık Hesap', bekleyen: 'Masada Bekleyen', masa: 'Masa', bos: 'Şu an açık hesap yok.', ...config.etiketler };

  const query = useWidgetData(sourceId, `open-tickets-${widget.reportCode}`, parameters);

  const tickets = useMemo<Ticket[]>(
    () =>
      (query.data ?? []).map((r) => ({
        tip: String(r.tip ?? ''),
        bolum: String(r.bolum ?? ''),
        masa: String(r.masa ?? ''),
        kapasite: num(r.kapasite),
        acilis: String(r.acilis ?? ''),
        acikDk: num(r.acik_dk),
        tutar: num(r.tutar),
      })),
    [query.data],
  );

  // tip → bölüm → satır. Bölümler açık adet desc, satırlar süre desc.
  // sectionOrder'da olmayan tip sona eklenir (yeni bir tip çıkarsa kaybolmasın).
  const sections = useMemo<TipSection[]>(() => {
    const byTip = new Map<string, Map<string, Ticket[]>>();
    for (const t of tickets) {
      const bolumler = byTip.get(t.tip) ?? new Map<string, Ticket[]>();
      const list = bolumler.get(t.bolum) ?? [];
      list.push(t);
      bolumler.set(t.bolum, list);
      byTip.set(t.tip, bolumler);
    }

    const rank = (tip: string) => {
      const i = sectionOrder.indexOf(tip);
      return i === -1 ? sectionOrder.length : i;
    };

    return Array.from(byTip.entries())
      .map(([tip, bolumMap]) => {
        const bolumler = Array.from(bolumMap.entries())
          .map(([bolum, rows]) => ({
            bolum,
            adet: rows.length,
            tutar: rows.reduce((a, r) => a + r.tutar, 0),
            rows: [...rows].sort((a, b) => b.acikDk - a.acikDk),
          }))
          .sort((a, b) => b.adet - a.adet);
        return {
          tip,
          adet: bolumler.reduce((a, b) => a + b.adet, 0),
          tutar: bolumler.reduce((a, b) => a + b.tutar, 0),
          bolumler,
        };
      })
      .sort((a, b) => rank(a.tip) - rank(b.tip) || a.tip.localeCompare(b.tip, 'tr'));
  }, [tickets, sectionOrder]);

  const toplamTutar = tickets.reduce((a, t) => a + t.tutar, 0);
  const enUzun = tickets.reduce<Ticket | null>(
    (best, t) => (best === null || t.acikDk > best.acikDk ? t : best),
    null,
  );

  return (
    <div data-testid="open-tickets-widget">
      <Panel title={config.title} subtitle={config.subtitle}>
        {!sourceId ? (
          // sourceId yoksa useWidgetData sorgusu `enabled: false` olur:
          // isLoading/isError hep false, data undefined → tickets.length===0
          // ile "Şu an açık hesap yok." dalına düşer ve hiç sorgulanmamışken
          // "hepsi kapandı" yalanı söyler. Bu yüzden isLoading kontrolünden
          // ÖNCE, ayrı ve nötr bir "kaynak bağlı değil" durumu gösteriyoruz.
          <Empty
            description="Rapor kaynağı bağlı değil."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: 40, color: 'var(--color-text-muted)' }}
          />
        ) : query.isLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : query.isError ? (
          // Boş sonuçtan AYRI mesaj: "0 açık hesap" bu widget'ta geçerli bir
          // sonuç, kırık sorgu onunla karıştırılırsa yanlış okunur.
          <Empty
            description="Açık hesap verisi alınamadı."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: 40, color: 'var(--color-warning)' }}
          />
        ) : tickets.length === 0 ? (
          <Empty description={etiket.bos} style={{ padding: 40 }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <Kpi
                testId="kpi-acik-adet"
                label={etiket.acik}
                value={numberFmt.format(tickets.length)}
              />
              <Kpi
                testId="kpi-bekleyen"
                label={etiket.bekleyen}
                value={currencyFmt.format(toplamTutar)}
              />
              <Kpi
                testId="kpi-en-uzun"
                label="En Uzun Açık"
                value={enUzun ? formatDuration(enUzun.acikDk) : '—'}
                hint={enUzun ? `${enUzun.bolum} ${enUzun.masa}` : undefined}
              />
            </div>

            {sections.map((s) => (
              <div key={s.tip} data-testid={`section-${s.tip}`}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '4px 2px 8px',
                    borderBottom: '1px solid var(--color-border)',
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-text-primary)' }}
                  >
                    {s.tip}
                  </span>
                  <span
                    className="tnum"
                    style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}
                  >
                    {numberFmt.format(s.adet)} açık · {currencyFmt.format(s.tutar)}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {s.bolumler.map((b) => (
                    <details
                      key={`${s.tip}-${b.bolum}`}
                      open
                      style={{
                        border: '1px solid var(--color-border)',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <summary
                        data-testid={`bolum-${b.bolum}`}
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '8px 12px',
                          cursor: 'pointer',
                          listStyle: 'none',
                          background: 'var(--color-bg-subtle, #f8fafc)',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: 'var(--color-text-primary)',
                          }}
                        >
                          {b.bolum}
                        </span>
                        <span
                          className="tnum"
                          style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                          }}
                        >
                          {numberFmt.format(b.adet)} açık · {currencyFmt.format(b.tutar)}
                        </span>
                      </summary>

                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead>
                          <tr style={{ color: 'var(--color-text-muted)', textAlign: 'left' }}>
                            <th style={thStyle}>{etiket.masa}</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Kap.</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Açılış</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Süre</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Tutar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {b.rows.map((t, i) => {
                            const esik = lateAfterHours[t.tip];
                            const late = esik !== undefined && t.acikDk >= esik * 60;
                            return (
                              <tr
                                key={`${t.bolum}-${t.masa}-${i}`}
                                style={{ borderTop: '1px solid var(--color-border)' }}
                              >
                                <td style={tdStyle}>{t.masa}</td>
                                <td className="tnum" style={{ ...tdStyle, textAlign: 'right' }}>
                                  {t.kapasite > 0 ? numberFmt.format(t.kapasite) : '—'}
                                </td>
                                <td className="tnum" style={{ ...tdStyle, textAlign: 'right' }}>
                                  {formatClock(t.acilis)}
                                </td>
                                <td
                                  className="tnum"
                                  // SQL bilerek eşleşmeyen satırları '(Bölümsüz)'/'(Masasız)'
                                  // olarak korur → bu değerlerden birden fazla satır olursa
                                  // `bolum-masa` tek başına benzersiz değil. Satır indeksi eklendi.
                                  data-testid={`sure-${t.bolum}-${t.masa}-${i}`}
                                  data-late={late ? 'true' : 'false'}
                                  style={{
                                    ...tdStyle,
                                    textAlign: 'right',
                                    color: late ? 'var(--color-negative)' : undefined,
                                    fontWeight: late ? 600 : undefined,
                                  }}
                                >
                                  {formatDuration(t.acikDk)}
                                </td>
                                <td
                                  className="tnum"
                                  style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}
                                >
                                  {currencyFmt.format(t.tutar)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

const thStyle: React.CSSProperties = { padding: '6px 8px', fontWeight: 600, fontSize: 11 };
const tdStyle: React.CSSProperties = { padding: '6px 8px', color: 'var(--color-text-primary)' };

function Kpi({
  testId,
  label,
  value,
  hint,
}: {
  testId: string;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </span>
      <span
        className="tnum"
        style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}
      >
        {value}
      </span>
      {hint && <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{hint}</span>}
    </div>
  );
}

export const OpenTicketsWidget = memo(OpenTicketsWidgetImpl);
